import axios from 'axios';
import { getUserToken } from 'utils/helpers';

const baseURL = process.env.NEXT_PUBLIC_BASE_URL + '/api';
const client = axios.create({
  baseURL,
});

// Create an instance of Axios
export const api = axios.create({
  baseURL, // replace with your API base URL
});

// Add a request interceptor
api.interceptors.request.use(
  (config) => {
    // Retrieve the bearer token from localStorage
    const token = getUserToken();
    config.headers = config.headers ?? {};
    if (token) {
      // Set the Authorization header with the bearer token
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      // Abort the request if the token is not available
      return Promise.reject(new Error('Token not available'));
    }

    return config;
  },
  (error) => Promise.reject(error)
);

export const AGENCY_TOKEN_KEY = 'agencyToken';

export const getAgencyToken = () =>
  typeof window === 'undefined'
    ? null
    : window.localStorage.getItem(AGENCY_TOKEN_KEY);

export const agencyApi = axios.create({ baseURL });

agencyApi.interceptors.request.use(
  (config) => {
    const token = getAgencyToken();
    config.headers = config.headers ?? {};
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

export const reviewClient = axios.create({
  baseURL,
  withCredentials: true,
});

/* ------------------------------------------------------------------ */
/* Screening product                                                   */
/* ------------------------------------------------------------------ */

export const SCREENING_TOKEN_KEY = 'screeningToken';

export const getScreeningToken = () =>
  typeof window === 'undefined'
    ? null
    : window.localStorage.getItem(SCREENING_TOKEN_KEY);

const screeningBaseURL = process.env.NEXT_PUBLIC_BASE_URL + '/api/v1';

export const screeningApi = axios.create({ baseURL: screeningBaseURL });

screeningApi.interceptors.request.use(
  (config) => {
    const token = getScreeningToken();
    config.headers = config.headers ?? {};
    if (typeof window !== 'undefined') {
      config.headers['X-HireMe-Public-Origin'] = window.location.origin;
    }
    // Only screening dashboard endpoints are authenticated; public endpoints
    // (board, apply, interview, webhooks) must not carry the bearer token.
    const url = config.url ?? '';
    const isScreeningScope = url.startsWith('/screening');
    if (token && isScreeningScope) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/* ------------------------------------------------------------------ */
/* Workforce product                                                   */
/* ------------------------------------------------------------------ */

// Workforce is a wholly separate product with its own credential. The token
// lives under a dedicated key and is only ever attached to Workforce calls —
// never Apollo's `userToken`.
export const WORKFORCE_TOKEN_KEY = 'workforce_token';

export const getWorkforceToken = () =>
  typeof window === 'undefined'
    ? null
    : window.localStorage.getItem(WORKFORCE_TOKEN_KEY);

// Browser requests stay same-origin; the Next proxy forwards this header to
// the workforce service.
const workforceBaseURL = '/api/workforce-proxy';

export const workforceApi = axios.create({ baseURL: workforceBaseURL });

workforceApi.interceptors.request.use(
  (config) => {
    const token = getWorkforceToken();
    config.headers = config.headers ?? {};
    if (typeof window !== 'undefined') {
      // Same header screening uses so invite emails point at the browser
      // origin (localhost / UAT / production) instead of a stale FRONTEND_URL.
      config.headers['X-HireMe-Public-Origin'] = window.location.origin;
    }
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

export default client;
